import { ArrowRightIcon, RefreshIcon } from "@xola/icons";
import dayjs from "dayjs";
import { isArray } from "lodash";
import React from "react";
import { Audit, Event, ExperiencePurchaseItem, Purchase, PurchaseItem } from "types";
import { ExperienceTile } from "../ExperienceTile";
import { TimelineItem, TimelinePill } from "../TimelineItem";
import { useEvent } from "@/hooks/resources/useEvent";
import { useSeller } from "@/hooks/useAccount";
import { getStart } from "@/models/Event";

interface AuditItemMatch {
    id: string;
    product?: { old: { id: string }; new: { id: string } };
    event?: { old?: { id: string }; new?: { id: string } };
    arrivalDateTime?: { old?: string; new?: string };
    arrivalTime?: { old?: number; new?: number };
}

const findAuditMatch = (audit: Audit, itemId: string): AuditItemMatch | undefined => {
    if (!isArray(audit.data?.items)) {
        return undefined;
    }

    return audit.data.items.find((auditItem: AuditItemMatch) => auditItem.id === itemId && auditItem.product);
};

interface SwitchExperienceTimelineItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
    showDate?: boolean;
}

export const SwitchExperienceTimelineItem = ({
    purchase,
    item,
    audit,
    showDate = false,
}: SwitchExperienceTimelineItemProps) => {
    const seller = useSeller();

    const match = findAuditMatch(audit, item?.id);
    const oldEventId = match?.event?.old?.id;
    const newEventId = match?.event?.new?.id;

    const { event: oldEvent } = useEvent(oldEventId, { enabled: !!oldEventId });
    const { event: newEvent } = useEvent(newEventId, { enabled: !!newEventId });

    const [from, to] = getExperiences(match, item as ExperiencePurchaseItem, seller.timezoneName, oldEvent, newEvent);

    if (!SwitchExperienceTimelineItem.supportsAudit(purchase, item, audit)) {
        return null;
    }

    return (
        <TimelineItem
            title="Switch Experiences"
            audit={audit}
            icon={<RefreshIcon />}
            color="primary"
            showDate={showDate}
            purchase={purchase}
        >
            <div className="flex items-center space-x-4">
                <TimelinePill>{from}</TimelinePill>
                <ArrowRightIcon />
                <TimelinePill>{to}</TimelinePill>
            </div>
        </TimelineItem>
    );
};

SwitchExperienceTimelineItem.supportsAudit = (_purchase: Purchase, item: PurchaseItem, audit: Audit) => {
    return !!findAuditMatch(audit, item?.id)?.product;
};

const getExperiences = (
    match: AuditItemMatch | undefined,
    item: ExperiencePurchaseItem,
    timezoneName: string,
    oldEvent?: Event,
    newEvent?: Event,
) => {
    const oldArrivalDateTime =
        match?.arrivalDateTime?.old ?? (oldEvent ? getStart(oldEvent) : undefined) ?? item.arrivalDateTime;
    const newArrivalDateTime =
        match?.arrivalDateTime?.new ?? (newEvent ? getStart(newEvent) : undefined) ?? item.arrivalDateTime;
    return match?.product
        ? [
              <ExperienceTile
                  key={0}
                  item={item}
                  experienceId={match.product.old?.id}
                  arrivalDateTime={dayjs(oldArrivalDateTime).tz(timezoneName)}
                  showTime={match.arrivalTime?.old !== null || "arrivalTime" in item}
              />,
              <ExperienceTile
                  key={1}
                  item={item}
                  experienceId={match.product.new?.id}
                  arrivalDateTime={dayjs(newArrivalDateTime).tz(timezoneName)}
                  showTime={match.arrivalTime?.new !== null || "arrivalTime" in item}
              />,
          ]
        : [null, null];
};
